import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { ActivatedRoute, RouterModule } from '@angular/router';
import {
  ComplianceAsset,
  ComplianceOverviewRequirementAsset,
  ComplianceOverviewRequirementAssetsApiRequest,
  ComplianceTasksTableRowAction,
  TableColumn
} from '../../../models/compliance-item.model';
import { ComplianceFiltersComponent } from '../../../../_shared/components/compliance-filters/compliance-filters.component';
import { NonCompliantWidgetComponent } from '../../../../_shared/widgets/non-compliant-widget/non-compliant-widget.component';
import { ExpiringSoonWidgetComponent } from '../../../../_shared/widgets/expiring-soon-widget/expiring-soon-widget.component';
import { CompliantWidgetComponent } from '../../../../_shared/widgets/compliant-widget/compliant-widget.component';
import { InspectionPassRateWidgetComponent } from '../../../../_shared/widgets/inspection-pass-rate-widget/inspection-pass-rate-widget.component';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { ComplianceOverviewService } from '../../../services/compliance-overview.service';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { ConfirmationModalService } from '../../../../../../_shared/services/modals/confirmation-modal.service';
import { ConfirmationOptions } from '../../../../../../_shared/models/modals/confirmation-options.model';
import { EmptyError, Subject, Subscription } from 'rxjs';
import { ComplianceStateService } from '../../../../_shared/services/compliance-state.service';
import { ComplianceTasksTableComponent } from '../../../../_shared/components/compliance-tasks/compliance-tasks-table.component';
import { ComplianceTasksTableAction } from '../../../../_shared/enums/compliance-tasks-table-action.enum';
import { ChecklistTypeIdEnum } from '../../../../_shared/models/checklist-type.model';
import { RequirementLayout } from '../../../models/requirement-layout.model';
import { buildComplianceAssetListColumns } from '../../../helpers/compliance-asset-list-columns.helper';
import { CompliancesLibraryService } from '../../../../compliance-library/services/compliances-library.service';
import { LinkedDocumentService } from '../../../../_shared/services/linked-document.service';
import { buildLinkedDocumentExpiryParts, buildLinkedDocumentLabel } from '../../../../_shared/helpers/linked-document.helper';
import { TranslocoService } from '@jsverse/transloco';
import { VehicleDocumentsService } from '../../../../../asset-overview/services/vehicle-documents.service';
import { EditComplianceDocumentModalComponent } from '../../../../../asset-overview/components/compliance/modals/edit-compliance-document-modal/edit-compliance-document-modal.component';

@Component({
  selector: 'app-compliance-asset-list',
  standalone: true,
  imports: [
    RouterModule,
    ComplianceFiltersComponent,
    NonCompliantWidgetComponent,
    ExpiringSoonWidgetComponent,
    CompliantWidgetComponent,
    InspectionPassRateWidgetComponent,
    CommonModule,
    ScrollingModule,
    NgbTooltipModule,
    ComplianceTasksTableComponent
  ],
  templateUrl: './compliance-asset-list.component.html',
  styleUrl: './compliance-asset-list.component.scss'
})
export class ComplianceAssetListComponent implements OnInit, OnDestroy {
  @ViewChild(ComplianceFiltersComponent) private complianceFilters?: ComplianceFiltersComponent;

  /** Assumed until the requirement is resolved, and used when it cannot be. */
  private static readonly defaultLayout: RequirementLayout = { checklistType: ChecklistTypeIdEnum.Document, hasLinkedDocumentType: true };

  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private compliancesLibraryService: CompliancesLibraryService = inject(CompliancesLibraryService);
  private complianceOverviewStateService: ComplianceStateService = inject(ComplianceStateService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private linkedDocumentService: LinkedDocumentService = inject(LinkedDocumentService);
  private vehicleDocumentsService: VehicleDocumentsService = inject(VehicleDocumentsService);
  private confirmationModalService: ConfirmationModalService = inject(ConfirmationModalService);
  private modalService: NgbModal = inject(NgbModal);
  private toastr: ToastrService = inject(ToastrService);
  private transloco: TranslocoService = inject(TranslocoService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private readonly cancelSubject = new Subject<void>();
  private readonly layoutCancelSubject = new Subject<void>();
  private requestSubscription?: Subscription;
  private columnsReady: Promise<void> = Promise.resolve();

  selectedRequirementName: string;
  selectedRequirementId: number;
  selectedComplianceName: string;
  selectedComplianceId: number;
  complianceAssets: ComplianceAsset[] = [];
  isLoading: boolean = true;
  isLoadingMore: boolean = false;
  hasError: boolean = false;
  private page: number = 1;
  private readonly pageSize: number = 10;
  private totalPages: number = 0;

  columns: TableColumn[] = buildComplianceAssetListColumns(ComplianceAssetListComponent.defaultLayout);

  ngOnInit(): void {
    this.complianceOverviewStateService.clearPageFilters({ searchTerm: true });

    this.selectedRequirementId = Number(this.route.snapshot.queryParamMap.get('requirementId') || 0);
    this.selectedRequirementName = this.route.snapshot.queryParamMap.get('requirementName') || '';

    this.selectedComplianceId = Number(this.route.snapshot.queryParamMap.get('complianceId') || 0);
    this.selectedComplianceName = this.route.snapshot.queryParamMap.get('complianceName') || '';

    this.columnsReady = this.applyRequirementLayoutAsync();

    this.requestSubscription = this.complianceOverviewStateService.request$.subscribe(() => {
      void this.reloadAssetsAsync();
    });
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
    this.layoutCancelSubject.next();
    this.layoutCancelSubject.complete();
    this.cancelSubject.next();
    this.cancelSubject.complete();

    this.complianceFilters?.clearAssetTypeFilter();
  }

  get hasMoreResults(): boolean {
    return this.page < this.totalPages;
  }

  async getComplianceAssetsAsync(resetResults: boolean = false): Promise<void> {
    try {
      if (this.page === 1 || resetResults) {
        this.isLoading = true;
        this.isLoadingMore = false;
        this.hasError = false;
        this.cancelSubject.next();
      } else {
        this.isLoadingMore = true;
      }

      const requestedPage = this.page;
      const response = await this.complianceOverviewService.getComplianceOverviewRequirementAssetsAsync(
        this.buildApiRequest(requestedPage),
        this.cancelSubject
      );
      await this.columnsReady;
      const data = response.data;
      const assets = (data?.results ?? []).map(asset => this.mapRequirementAsset(asset));

      this.complianceAssets = requestedPage === 1
        ? assets
        : [...this.complianceAssets, ...assets];
      this.totalPages = data?.totalPages ?? 0;
      this.isLoading = false;
      this.isLoadingMore = false;
    } catch (error) {
      if (error instanceof EmptyError) {
        return;
      }

      if (this.page > 1) {
        this.page--;
      }

      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
          this.isLoadingMore = false;
          this.hasError = true;
        }
      });
    }
  }

  onScrollDown(): void {
    if (this.isLoading || this.isLoadingMore || !this.hasMoreResults) {
      return;
    }

    this.page++;
    void this.getComplianceAssetsAsync();
  }

  onActionClick(event: { item: ComplianceAsset; action: ComplianceTasksTableAction }): void {
    switch (event.action) {
      case ComplianceTasksTableAction.EditDocument:
        void this.editLinkedDocumentAsync(event.item);
        break;
      case ComplianceTasksTableAction.DeleteDocument:
        this.confirmDeleteLinkedDocument(event.item);
        break;
    }
  }

  onLinkClick(event: { item: ComplianceAsset; column: TableColumn }): void {
    if (event.column.value === 'document') {
      void this.viewLinkedDocumentAsync(event.item);
    }
  }

  private async applyRequirementLayoutAsync(): Promise<void> {
    const layout = await this.resolveRequirementLayoutAsync();
    this.columns = buildComplianceAssetListColumns(layout);
  }

  private async resolveRequirementLayoutAsync(): Promise<RequirementLayout> {
    const fallback = ComplianceAssetListComponent.defaultLayout;
    if (!this.selectedComplianceId || !this.selectedRequirementId) {
      return fallback;
    }

    try {
      const [checklistsResponse, linksResponse] = await Promise.all([
        this.compliancesLibraryService.getChecklistsIncludingDisabledAsync(
          { complianceId: this.selectedComplianceId, isArchived: false },
          this.layoutCancelSubject
        ),
        this.complianceOverviewService.getRequirementDocumentLinksAsync(this.selectedComplianceId)
      ]);

      const checklist = (checklistsResponse.data ?? [])
        .find(item => item.requirements.some(requirement => requirement.id === this.selectedRequirementId));
      if (!checklist) {
        return fallback;
      }

      return {
        checklistType: checklist.checklistTypeId as ChecklistTypeIdEnum,
        hasLinkedDocumentType: (linksResponse.data ?? []).some(link => link.requirementId === this.selectedRequirementId)
      };
    } catch (error) {
      this.errorService.handleError(error, { showToast: false });
      return fallback;
    }
  }

  private async viewLinkedDocumentAsync(asset: ComplianceAsset): Promise<void> {
    if (!asset.assetId || !asset.linkedDocument) {
      return;
    }

    await this.linkedDocumentService.openAsync(asset.assetId, asset.linkedDocument, this.cancelSubject);
  }

  private async editLinkedDocumentAsync(asset: ComplianceAsset): Promise<void> {
    if (!asset.assetId || !asset.linkedDocumentId) {
      return;
    }

    const assetId = asset.assetId;
    await this.linkedDocumentService.withDocumentAsync(assetId, asset.linkedDocumentId, vehicleDocument => {
      const modalRef = this.modalService.open(EditComplianceDocumentModalComponent, {
        centered: true,
        backdrop: 'static'
      });
      modalRef.componentInstance.document = vehicleDocument;
      modalRef.componentInstance.assetId = assetId;

      modalRef.closed.subscribe((updated: boolean) => {
        if (updated) {
          void this.reloadAfterDocumentChangeAsync();
        }
      });
    }, this.cancelSubject);
  }

  private confirmDeleteLinkedDocument(asset: ComplianceAsset): void {
    const assetId = asset.assetId;
    const documentId = asset.linkedDocumentId;
    if (!assetId || !documentId) {
      return;
    }

    const options: ConfirmationOptions = {
      title: this.transloco.translate('compliance.overview.assetList.confirmations.deleteDocument.title'),
      message: this.transloco.translate('compliance.overview.assetList.confirmations.deleteDocument.message'),
      warningMessage: this.transloco.translate('compliance.overview.assetList.confirmations.deleteDocument.warning'),
      confirmText: this.transloco.translate('compliance.overview.assetList.confirmations.deleteDocument.confirm'),
      declineText: this.transloco.translate('compliance.overview.assetList.confirmations.deleteDocument.decline'),
      confirmPlaceholder: this.transloco.translate('compliance.overview.assetList.confirmations.deleteDocument.placeholder'),
      confirmationPrompt: 'delete',
      onConfirm: async () => {
        try {
          await this.vehicleDocumentsService.deleteDocument({ documentId, assetId });
          this.toastr.success(this.transloco.translate('compliance.overview.assetList.toasts.documentDeleted'));
          void this.reloadAfterDocumentChangeAsync();
        } catch (error) {
          this.errorService.handleError(error);
        }
      }
    };

    this.confirmationModalService.showConfirmationModal(options);
  }

  private reloadAfterDocumentChangeAsync(): Promise<void> {
    this.complianceOverviewStateService.syncWidgetFilter();
    return this.reloadAssetsAsync();
  }

  private reloadAssetsAsync(): Promise<void> {
    this.page = 1;
    this.totalPages = 0;
    this.complianceAssets = [];
    return this.getComplianceAssetsAsync(true);
  }

  private buildApiRequest(page: number): ComplianceOverviewRequirementAssetsApiRequest {
    const filters = this.complianceOverviewStateService.getCurrentRequest();
    return {
      accountIds: filters?.accountIds ?? [],
      page,
      pageSize: this.pageSize,
      searchTerm: filters?.searchTerm ?? null,
      complianceRequirementId: this.selectedRequirementId,
      assetTypeId: filters?.assetTypeId ?? undefined
    };
  }

  private mapRequirementAsset(asset: ComplianceOverviewRequirementAsset): ComplianceAsset {
    return {
      assetId: asset.assetId,
      assetName: asset.assetName,
      complianceType: asset.complianceName,
      document: buildLinkedDocumentLabel(asset.linkedDocument) || this.transloco.translate('compliance.overview.assetList.noDocument'),
      documentExpiry: this.buildDocumentExpiryLabel(asset.linkedDocument?.validUntil),
      linkedDocumentId: asset.linkedDocument?.documentId ?? null,
      linkedDocument: asset.linkedDocument ?? null,
      actions: this.buildDocumentActions(asset),
      dateIssued: asset.linkedDocument?.lastUpdatedAt ? this.formatDisplayDate(asset.linkedDocument.lastUpdatedAt) : '-',
      lastInspectionDate: asset.lastInspectionDate ? this.formatDisplayDate(asset.lastInspectionDate) : '-',
      overdueDays: this.formatDaysOverdue(asset.daysOverdue),
      status: asset.status
    };
  }

  private buildDocumentActions(asset: ComplianceOverviewRequirementAsset): ComplianceTasksTableRowAction[] {
    if (!asset.linkedDocument) {
      return [];
    }

    return [
      { label: this.transloco.translate('compliance.overview.assetList.actions.editDocument'), action: ComplianceTasksTableAction.EditDocument },
      { label: this.transloco.translate('compliance.overview.assetList.actions.deleteDocument'), action: ComplianceTasksTableAction.DeleteDocument, danger: true }
    ];
  }

  private formatDisplayDate(value: string | Date): string {
    return formatDate(value, 'dd/MM/yyyy', 'en-ZA');
  }

  private formatDaysOverdue(days: number): string {
    return this.transloco.translate(
      days === 1
        ? 'compliance.overview.assetList.dayCountOne'
        : 'compliance.overview.assetList.dayCountMany',
      { count: days }
    );
  }

  private buildDocumentExpiryLabel(validUntil: string | Date | null | undefined): string {
    const parts = buildLinkedDocumentExpiryParts(validUntil);
    return parts ? this.transloco.translate(parts.key, { date: parts.date }) : '';
  }
}